// React & React ecosystem
import { useNavigate } from 'react-router-dom';

// External libraries (alphabetically)
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// Internal absolute imports (@/ aliases)
// Stores
import { useAuthStore } from '@/store/authStore';

// Lib utilities
import { ErrorHandler } from '@/lib/errors/errorHandler';

// Relative imports from same feature
import { authService } from '../services/authService';
import type { RegisterRequest } from '../types';

/**
 * DOCU: React Query mutation hook for user registration with auto-login. <br>
 * Triggered: Called when user submits RegistrationForm. <br>
 * Last Updated Date: December 1, 2025 <br>
 * @returns Mutation object with register function, loading state, and error handling.
 * @author Kirt, Updated by: Sam
 *
 * @example
 * ```tsx
 * const { mutate: register, isPending } = useRegister();
 *
 * const handleSubmit = (data: RegisterRequest) => {
 *   register(data);
 * };
 * ```
 */
export const useRegister = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: (data: RegisterRequest) => authService.register(data),
    onSuccess: (response) => {
      const { user, access_token, refresh_token } = response.result;

      if (!user) {
        toast.error('Invalid registration response. Please login to continue.');
        navigate('/login');
        return;
      }

      // Auto-login after successful registration
      if (access_token) {
        // Set user in Zustand store (which persists to localStorage)
        // Pass refresh_token if available
        setUser(user, access_token, refresh_token);

        toast.success(`Account created successfully! Welcome, ${user.first_name}!`);

        // Invalidate queries that depend on auth
        queryClient.invalidateQueries();

        // Small delay to ensure Zustand persist completes before navigation
        setTimeout(() => {
          navigate('/dashboard');
        }, 100);
      }
      else {
        // Fallback: redirect to login if no token provided
        toast.success(
          `Account created successfully! Welcome, ${user.first_name}! Please login to continue.`
        );
        navigate('/login');
      }
    },
    onError: (error) => {
      ErrorHandler.toast(error, 'Registration failed. Please try again.');
    },
  });
};
